import { AxiosResponse } from 'axios';

import { useQueryClient } from '@tanstack/react-query';

import { TypPayStatementTab, TypeCodeState } from '../../types';
import { TypeRequestHeaders, axiosInstance } from '~/services/fetchServiceV2';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

type TypeGetPayStatementsByRangeParams = {
  searchStartDate?: string;
  searchEndDate?: string;
  type?: Omit<TypPayStatementTab, 'MONTHLY'>;
};

type TypeGetPayStatementsByRangeResponse<T> = {
  code: string;
  message: string;
  data: {
    details: T[];
  };
};

export type TypePayStatementByPerDiem = {
  centerName: string;
  workDate: string;
  totalPay: number;
  deduction: number;
  net: number;
  paymentAt: string;
};

export type TypePayStatementByEtc = {
  payslipType: TypeCodeState;
  totalPay: number;
  deduction: number;
  net: number;
  paymentAt: string;
};

/** 일급, 추가수당 급여명세서 목록 조회 */
export const useGetPayStatementsByRange = () => {
  const queryClient = useQueryClient();

  const requestGetPayStatementsByRange = async ({
    params,
    headers,
    onSuccess,
    onError,
  }: {
    params: TypeGetPayStatementsByRangeParams;
    headers?: TypeRequestHeaders;
    onSuccess?: (
      response: TypeGetPayStatementsByRangeResponse<TypePayStatementByPerDiem | TypePayStatementByEtc>
    ) => void;
    onError?: (error: TypeErrorFormat) => void;
  }) => {
    const removeTypeParam = (params: TypeGetPayStatementsByRangeParams) => {
      delete params.type;
      return params;
    };

    const endpoint = '/labor/payslips';
    const query = queryClient.ensureQueryData<
      AxiosResponse<TypeGetPayStatementsByRangeResponse<TypePayStatementByPerDiem | TypePayStatementByEtc>>
    >({
      queryKey: ['payStatementsByRange', params.type, params.searchStartDate, params.searchEndDate],
      queryFn: () =>
        axiosInstance.get(`${endpoint}${params.type === 'ETC' ? '/etc' : '/per-diem'}`, {
          params: removeTypeParam(params),
          headers,
        }),
      gcTime: 0,
    });

    return requestHelper(() => query, onSuccess, onError);
  };

  return requestGetPayStatementsByRange;
};
